import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { postContactRequest } from '@/api';
import Button from '@/components/Button';
import Message from '@/components/Message';
import TextInput from './TextInput';
import validationSchema from './validationSchema';
import { extractValidateErrors, hasValidErrors, createContactRequest } from './utils';
import { INITIAL_VALUES, FIELDS } from './const';
import { useAsyncState } from './hooks';
import { Container, Form, FormFields } from './styles';

const ContactForm = ({ id }) => {
  const { t } = useTranslation(['contact', 'form']);

  const [state, setState] = useAsyncState(INITIAL_VALUES);
  const [alerts, setAlerts] = useAsyncState({});

  const postData = useCallback((data) => {
    postContactRequest(createContactRequest(id, data))
      .then(() => {
        setAlerts({ success: true });
        setState(INITIAL_VALUES);
      })
      .catch(() => setAlerts({ error: true }));
  },
  [id, setAlerts, setState]);

  const handleValidationErrors = useCallback(
    (errors) => {
      setAlerts({ errors: extractValidateErrors(errors) });
    },
    [setAlerts],
  );

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      const nextState = { ...state, [name]: value };
      setState(nextState);

      if (hasValidErrors(alerts.errors)) {
        validationSchema
          .validate(nextState, { abortEarly: false })
          .then(setAlerts({ errors: {} }))
          .catch(handleValidationErrors);
      }
    },
    [alerts.errors, handleValidationErrors, setAlerts, setState, state],
  );

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setAlerts({ loading: true });

    validationSchema
      .validate(state, { abortEarly: false })
      .then(postData)
      .catch(handleValidationErrors);
  },
  [handleValidationErrors, postData, setAlerts, state]);

  const defaultProps = useMemo(() => ({
    state,
    onChange: handleChange,
    errors: alerts.errors,
  }), [alerts.errors, handleChange, state]);

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <FormFields>
          {FIELDS.map((item) => (
            <TextInput
              id={item.id}
              key={item.id}
              placeholder={(item.isMessage && id === 'partner') ? t('messagePartner') : t(item.id)}
              name={item.id}
              value={state[item.id]}
              isMessage={item.isMessage}
              {...defaultProps}
            />
          ))}
        </FormFields>
        <Button
          width="100%"
          type="submit"
          disabled={hasValidErrors(alerts.errors) || alerts.loading}
        >
          {t('send')}
        </Button>
        {alerts.error && <Message error>{t('form:error')}</Message>}
        {(alerts.success && !alerts.error) && <Message noMargin={false}>{t('success')}</Message>}
      </Form>
    </Container>
  );
};

ContactForm.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ContactForm;
